<template>
	<LayoutScreen background="--background-base" max-width="500px" content-align="center">
		<template #content>
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)" align-items="center">
				<div class="verifying__icon" role="status" :aria-label="title">
					<img class="verifying__spinner" :src="spinner" alt="" width="35.7" height="35.591" />
				</div>
				<div :style="titleWidth ? { maxWidth: titleWidth } : undefined">
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">{{ title }}</Text>
				</div>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="0">
				<LayoutFlex
					padding="var(--size-12) var(--size-16)"
					gap="0"
					background="--background-alpha-low"
					corner-radius="var(--size-20)"
					min-width="100%"
				>
					<Text font="--text-16-20-med" color="--text-dark-secondary" text-align="center">
						{{ hint }}
					</Text>
				</LayoutFlex>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
// Full-screen "Verifying your payment method" content (Figma node 30633-7487). Hosted in
// a fullscreen DS <Modal> by UpdatePaymentMethod.vue while /payment-method/change (incl.
// 3DS) is in flight — it is NOT a route: the PaymentClient must stay mounted, and the
// design explicitly asks the user not to navigate.
//
// The copy is overridable so the commitment exit fee charge (/cancel/commitment/end-now, DEV-35727)
// reuses the same takeover as "Processing your payment..." (Figma 6455:21838, now 7415:17655).
import { ref } from 'vue'
import { LayoutFlex, LayoutScreen, Text } from '@simple-life-app/growth-ui-kova'
import spinner from '@/assets/icons/spinner.png'
import { usePageScrollLock } from '@/composables/usePageScrollLock'

// The host's fullscreen <Modal> renders this only while visible; the page behind it must not scroll meanwhile.
usePageScrollLock(ref(true))

withDefaults(
	defineProps<{
		title?: string
		hint?: string
		/** The column the title wraps in — 7415:17655 sets "Processing your payment..." in 272px (two lines). */
		titleWidth?: string
	}>(),
	{
		title: 'Verifying your payment method',
		hint: "Please don't go back or refresh the page while we verify your payment method.",
		titleWidth: undefined,
	},
)
</script>

<style scoped>
/* Figma icon-loading: the Kova ring with a fading tail (an image in the design), 35.7px in a 42px box
   with 20 around it; it turns while the charge is in flight. */
.verifying__icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 82px;
	height: 82px;
}

.verifying__spinner {
	animation: verifying-spin 0.9s linear infinite;
}

@keyframes verifying-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
